/**
 * Planos — Tela de planos e preços do Escalone.
 * Layout idêntico ao Step 3 do Cadastro.
 */
import React, { useState } from 'react';
import {
  Zap,
  Crown,
  Check,
  X,
  ChevronRight,
  Sparkles,
  ArrowLeft,
  Building2,
  GitBranch,
  Users,
  Bot,
} from 'lucide-react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';

interface PlanosProps {
  planoInfo?: any;
  empresaNome?: string;
  onVoltar?: () => void;
  onContratarPlano?: (plano: PlanoSelecionado) => void;
  modo?: 'pagina' | 'modal';
  session?: { usuario?: { role?: string } } | null;
}

export interface PlanoSelecionado {
  plano: string;
  periodo: 'mensal' | 'anual';
  usuarios: number;
  precoTotal: number;
  precoUnitario: number;
}

const PRECO_MENSAL = 80;
const PRECO_ANUAL = 72; // -10%
const PRECO_ANUAL_TOTAL_USUARIO = 864; // 72 * 12
const ECONOMIA_POR_USUARIO_ANO = 96; // (80-72) * 12

const Planos: React.FC<PlanosProps> = ({
  planoInfo,
  empresaNome,
  onVoltar,
  onContratarPlano,
  modo = 'pagina',
  session,
}) => {
  const [periodo, setPeriodo] = useState<'mensal' | 'anual'>('anual');

  // Mínimo = uso atual de colaboradores (mínimo absoluto = 1)
  const usoAtual = planoInfo?.uso?.colaboradores ?? 0;
  const minUsuarios = Math.max(1, usoAtual);
  const [qtd, setQtd] = useState(minUsuarios);

  const preco = periodo === 'mensal' ? PRECO_MENSAL : PRECO_ANUAL;
  const total = qtd * preco;

  const planoAtual = planoInfo?.plano || 'free_trial';
  const trialExpirado = planoInfo?.trial_expirado === true;
  const trialAtivo = planoInfo?.trial_ativo === true;
  const diasRestantes = planoInfo?.dias_restantes ?? 0;

  const inc = () => setQtd(q => Math.min(q + 1, 999));
  const dec = () => setQtd(q => Math.max(q - 1, minUsuarios));

  const handleAssinar = () => {
    onContratarPlano?.({
      plano: 'pro',
      periodo,
      usuarios: qtd,
      precoTotal: periodo === 'mensal' ? total : qtd * PRECO_ANUAL_TOTAL_USUARIO,
      precoUnitario: preco,
    });
  };

  const handleEnterprise = () => {
    const msg = encodeURIComponent(
      `Olá! Tenho interesse no plano Enterprise do Escalone. Empresa: ${empresaNome || 'N/A'}`
    );
    window.open(`https://wa.me/5511999999999?text=${msg}`, '_blank');
  };

  const content = (
    <div className="min-h-screen bg-gray-50/50">
      {/* ── Header ── */}
      <div className="sticky top-0 z-10 bg-gray-50/95 backdrop-blur-sm">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-8 pb-4">
          {/* Logo + Title */}
          <div className="text-center mb-2">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-gray-900 to-black text-white flex items-center justify-center font-extrabold text-xl shadow-lg">
                E
              </div>
              <span className="font-extrabold text-2xl text-gray-900 tracking-tight">Escalone</span>
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Escolha seu plano</h1>
            {empresaNome && (
              <p className="text-sm text-gray-500 mt-1">{empresaNome}</p>
            )}
            {trialAtivo && (
              <p className="text-sm text-indigo-600 font-medium mt-1">
                Trial — {diasRestantes} dia{diasRestantes !== 1 ? 's' : ''} restante{diasRestantes !== 1 ? 's' : ''}
              </p>
            )}
            {trialExpirado && (
              <p className="text-sm text-red-600 font-medium mt-1">
                Seu período de teste expirou
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 pb-12">

        {/* ── Painel de uso atual ── */}
        {(planoAtual === 'free_trial') && (
          <div className="mb-8 bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-sm font-bold text-gray-900">Uso atual do plano</h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  {trialAtivo
                    ? `Free Trial · ${diasRestantes} dia${diasRestantes !== 1 ? 's' : ''} restante${diasRestantes !== 1 ? 's' : ''}`
                    : trialExpirado
                    ? 'Free Trial · Expirado'
                    : 'Plano Free'}
                </p>
              </div>
              {trialExpirado && (
                <span className="text-xs font-bold bg-red-50 text-red-600 border border-red-200 px-3 py-1 rounded-full">
                  Expirado
                </span>
              )}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                {
                  icon: Building2,
                  label: 'Empresas',
                  uso: planoInfo?.uso?.empresas ?? 0,
                  limite: planoInfo?.limites?.empresas ?? 0,
                  color: 'indigo',
                },
                {
                  icon: GitBranch,
                  label: 'Fluxos',
                  uso: planoInfo?.uso?.fluxos ?? 0,
                  limite: planoInfo?.limites?.fluxos ?? 0,
                  color: 'violet',
                },
                {
                  icon: Users,
                  label: 'Colaboradores',
                  uso: planoInfo?.uso?.colaboradores ?? 0,
                  limite: planoInfo?.limites?.colaboradores ?? 0,
                  color: 'sky',
                },
                {
                  icon: Bot,
                  label: 'Automações',
                  uso: planoInfo?.uso?.automacoes ?? 0,
                  limite: planoInfo?.limites?.automacoes ?? 0,
                  color: 'amber',
                },
              ].map((item) => {
                const pct = Math.min(100, (item.uso / Math.max(item.limite, 1)) * 100);
                const atLimit = item.uso >= item.limite;
                const nearLimit = item.uso >= item.limite * 0.8;
                const Icon = item.icon;

                const barColor = atLimit
                  ? 'bg-red-500'
                  : nearLimit
                  ? 'bg-amber-500'
                  : item.color === 'indigo'
                  ? 'bg-indigo-500'
                  : item.color === 'violet'
                  ? 'bg-violet-500'
                  : item.color === 'sky'
                  ? 'bg-sky-500'
                  : 'bg-amber-500';

                const iconBg = atLimit
                  ? 'bg-red-50'
                  : item.color === 'indigo'
                  ? 'bg-indigo-50'
                  : item.color === 'violet'
                  ? 'bg-violet-50'
                  : item.color === 'sky'
                  ? 'bg-sky-50'
                  : 'bg-amber-50';

                const iconColor = atLimit
                  ? 'text-red-500'
                  : item.color === 'indigo'
                  ? 'text-indigo-500'
                  : item.color === 'violet'
                  ? 'text-violet-500'
                  : item.color === 'sky'
                  ? 'text-sky-500'
                  : 'text-amber-500';

                return (
                  <div key={item.label} className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                    <div className="flex items-center gap-2 mb-3">
                      <div className={`w-8 h-8 rounded-lg ${iconBg} flex items-center justify-center`}>
                        <Icon className={`w-4 h-4 ${iconColor}`} />
                      </div>
                      <span className="text-xs font-semibold text-gray-700">{item.label}</span>
                    </div>
                    <div className="flex items-baseline gap-1 mb-2">
                      <span className={`text-2xl font-extrabold ${atLimit ? 'text-red-600' : 'text-gray-900'}`}>
                        {item.uso}
                      </span>
                      <span className="text-sm text-gray-400">/ {item.limite}</span>
                    </div>
                    <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-500 ${barColor}`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    {atLimit && (
                      <p className="text-[10px] text-red-500 font-medium mt-1.5">Limite atingido</p>
                    )}
                  </div>
                );
              })}
            </div>

            <p className="text-xs text-gray-400 mt-4 text-center">
              Faça upgrade para o Pro e tenha recursos ilimitados ↓
            </p>
          </div>
        )}

        {/* ── Toggle mensal / anual ── */}
        <div className="flex items-center justify-center mb-8">
          <div className="bg-gray-100 rounded-full p-1 inline-flex items-center relative">
            <button
              onClick={() => setPeriodo('mensal')}
              className={`px-5 py-2 rounded-full text-sm font-semibold transition-all duration-200 relative z-10 ${
                periodo === 'mensal'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Mensal
            </button>
            <button
              onClick={() => setPeriodo('anual')}
              className={`px-5 py-2 rounded-full text-sm font-semibold transition-all duration-200 relative z-10 flex items-center gap-1.5 ${
                periodo === 'anual'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Anual
              <span className="bg-emerald-100 text-emerald-700 text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                -10%
              </span>
            </button>
          </div>
        </div>

        {/* ── 3 cards lado a lado ── */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 items-stretch">

          {/* ▸ Card Free / Trial */}
          <div className="bg-white rounded-2xl border border-gray-200 p-7 flex flex-col hover:shadow-lg transition-all duration-300 group">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-11 h-11 rounded-xl bg-emerald-50 flex items-center justify-center group-hover:scale-105 transition-transform">
                <Zap className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Free</h3>
                <p className="text-xs text-gray-500">Para conhecer a plataforma</p>
              </div>
            </div>

            <div className="mb-4 pb-4 border-b border-gray-100">
              <span className="text-4xl font-extrabold text-gray-900">R$ 0</span>
              <p className="text-sm text-gray-500 mt-1.5">7 dias grátis · Sem cartão</p>
            </div>

            <p className="text-xs text-emerald-600 font-medium bg-emerald-50 rounded-xl px-4 py-2.5 mb-5 leading-relaxed">
              Experimente nosso sistema sem compromisso e veja como ele pode transformar seus processos. 🚀
            </p>

            <ul className="space-y-3 mb-7 flex-1">
              {['7 dias de acesso completo', 'Até 5 fluxos de trabalho', 'Até 10 colaboradores', 'Até 10 automações por fluxo'].map((item) => (
                <li key={item} className="flex items-start gap-2.5 text-sm text-gray-600">
                  <div className="w-5 h-5 rounded-full bg-emerald-50 flex items-center justify-center shrink-0 mt-0.5">
                    <Check className="w-3 h-3 text-emerald-600" />
                  </div>
                  <span>{item}</span>
                </li>
              ))}
              {['Automações limitadas', 'Anexos limitados'].map((item) => (
                <li key={item} className="flex items-start gap-2.5 text-sm text-gray-400">
                  <div className="w-5 h-5 rounded-full bg-gray-50 flex items-center justify-center shrink-0 mt-0.5">
                    <X className="w-3 h-3 text-gray-300" />
                  </div>
                  <span>{item}</span>
                </li>
              ))}
            </ul>

            {planoAtual === 'free_trial' && !trialExpirado ? (
              <div className="w-full py-3 px-4 rounded-xl text-sm font-semibold text-gray-500 bg-gray-50 border border-gray-200 text-center">
                Plano atual
              </div>
            ) : (
              <div className="w-full py-3 px-4 rounded-xl text-sm font-semibold text-gray-400 bg-gray-50 border border-gray-200 text-center">
                —
              </div>
            )}
          </div>

          {/* ▸ Card Pro — destaque */}
          <div className="bg-white rounded-2xl border-2 border-indigo-600 p-7 flex flex-col relative shadow-xl shadow-indigo-500/10">
            <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
              <span className="bg-indigo-600 text-white text-xs font-bold px-4 py-1.5 rounded-full shadow-sm inline-flex items-center gap-1">
                <Crown className="w-3 h-3" /> Recomendado
              </span>
            </div>

            <div className="flex items-center gap-3 mb-5 mt-1">
              <div className="w-11 h-11 rounded-xl bg-indigo-100 flex items-center justify-center">
                <Crown className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Pro</h3>
                <p className="text-xs text-gray-500">Para equipes em crescimento</p>
              </div>
            </div>

            {/* Preço */}
            <div className="mb-5 pb-5 border-b border-indigo-100">
              {periodo === 'mensal' ? (
                <>
                  <div className="flex items-baseline gap-1">
                    <span className="text-4xl font-extrabold text-gray-900">R$ {PRECO_MENSAL}</span>
                    <span className="text-sm text-gray-500">/ usuário / mês</span>
                  </div>
                  <p className="text-sm text-indigo-600 font-medium mt-1.5">Cobrança mensal</p>
                </>
              ) : (
                <>
                  <div className="flex items-baseline gap-1">
                    <span className="text-4xl font-extrabold text-gray-900">R$ {PRECO_ANUAL}</span>
                    <span className="text-sm text-gray-500">/ usuário / mês</span>
                  </div>
                  <div className="flex items-center gap-2 mt-1.5">
                    <span className="text-sm text-gray-400 line-through">R$ {PRECO_MENSAL}</span>
                    <span className="bg-emerald-100 text-emerald-700 text-xs font-bold px-2 py-0.5 rounded-full">
                      Economize 10%
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">R$ {PRECO_ANUAL_TOTAL_USUARIO}/usuário cobrado anualmente</p>
                </>
              )}
            </div>

            {/* Simulador de usuários */}
            <div className="mb-5 pb-5 border-b border-indigo-100">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 block">Simule seu investimento</label>
              <div className="flex items-center gap-3">
                <button
                  onClick={dec}
                  disabled={qtd <= minUsuarios}
                  className="w-8 h-8 rounded-lg bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-600 font-bold transition-colors text-lg disabled:opacity-30 disabled:cursor-not-allowed"
                >−</button>
                <div className="flex-1 text-center">
                  <input
                    type="number"
                    min={minUsuarios}
                    max={999}
                    value={qtd}
                    onChange={e => {
                      const v = parseInt(e.target.value) || minUsuarios;
                      setQtd(Math.max(minUsuarios, Math.min(999, v)));
                    }}
                    className="w-16 text-center text-2xl font-extrabold text-gray-900 border-0 bg-transparent outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  />
                  <p className="text-[11px] text-gray-500 -mt-0.5">usuários</p>
                </div>
                <button
                  onClick={inc}
                  className="w-8 h-8 rounded-lg bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-600 font-bold transition-colors text-lg"
                >+</button>
              </div>

              {minUsuarios > 1 && (
                <p className="text-[11px] text-amber-600 bg-amber-50 border border-amber-100 rounded-lg px-3 py-1.5 mt-2 text-center">
                  Mínimo de {minUsuarios} usuário{minUsuarios > 1 ? 's' : ''} (colaboradores ativos)
                </p>
              )}

              <div className="bg-indigo-50 rounded-xl p-4 mt-3">
                {periodo === 'mensal' ? (
                  <div className="text-center">
                    <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Total estimado mensal</p>
                    <p className="text-3xl font-extrabold text-indigo-600 mt-1">
                      R$ {(qtd * PRECO_MENSAL).toLocaleString('pt-BR')}
                      <span className="text-sm font-semibold text-gray-500">/mês</span>
                    </p>
                  </div>
                ) : (
                  <div className="text-center">
                    <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Total estimado anual</p>
                    <p className="text-3xl font-extrabold text-indigo-600 mt-1">
                      R$ {(qtd * PRECO_ANUAL_TOTAL_USUARIO).toLocaleString('pt-BR')}
                      <span className="text-sm font-semibold text-gray-500">/ano</span>
                    </p>
                    <div className="flex items-center justify-center gap-3 mt-2 pt-2 border-t border-indigo-100">
                      <div>
                        <p className="text-[10px] text-gray-400 uppercase">Por mês</p>
                        <p className="text-sm font-bold text-gray-700">R$ {(qtd * PRECO_ANUAL).toLocaleString('pt-BR')}</p>
                      </div>
                      <div className="w-px h-7 bg-indigo-200" />
                      <div>
                        <p className="text-[10px] text-gray-400 uppercase">Economia</p>
                        <p className="text-sm font-bold text-emerald-600">R$ {(qtd * ECONOMIA_POR_USUARIO_ANO).toLocaleString('pt-BR')}/ano</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <ul className="space-y-3 mb-7 flex-1">
              {[
                { text: 'Empresas ilimitadas', bold: true },
                { text: 'Usuários ilimitados', bold: true },
                { text: 'Fluxos ilimitados', bold: true },
                { text: 'Automações completas', bold: false },
                { text: 'Anexos ilimitados', bold: false },
              ].map(({ text, bold }) => (
                <li key={text} className="flex items-start gap-2.5 text-sm text-gray-700">
                  <div className="w-5 h-5 rounded-full bg-indigo-50 flex items-center justify-center shrink-0 mt-0.5">
                    <Check className="w-3 h-3 text-indigo-600" />
                  </div>
                  <span className={bold ? 'font-semibold' : ''}>{text}</span>
                </li>
              ))}
            </ul>

            {/* CTA */}
            <button
              onClick={handleAssinar}
              className="w-full py-3 px-4 rounded-xl text-sm font-bold text-white bg-indigo-600 hover:bg-indigo-700 transition-all shadow-sm hover:shadow-md flex items-center justify-center gap-2"
            >
              Escolher Pro <ChevronRight className="w-4 h-4" />
            </button>
          </div>

          {/* ▸ Card Enterprise */}
          <div className="relative bg-white rounded-2xl border border-gray-200 p-7 flex flex-col hover:shadow-lg transition-all duration-300 group h-full">
            <div className="absolute top-4 right-4">
              <Sparkles className="w-5 h-5 text-indigo-300" />
            </div>

            <div className="flex items-center gap-3 mb-3">
              <div className="w-11 h-11 rounded-xl bg-indigo-50 flex items-center justify-center group-hover:scale-105 transition-transform">
                <Crown className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Enterprise</h3>
                <p className="text-xs text-gray-500">Para grandes operações</p>
              </div>
            </div>

            <p className="text-xs text-gray-500 mb-3 leading-relaxed">
              Ideal para empresas com <span className="font-semibold text-indigo-600">20+ colaboradores</span>, múltiplos processos e necessidade de integrações avançadas.
            </p>
            <p className="text-xs text-indigo-600 font-semibold mb-5 leading-relaxed">
              Desenvolvemos o seu processo do zero — do fluxo à integração.
            </p>

            <div className="mb-5 pb-5 border-b border-gray-100">
              <span className="text-4xl font-extrabold text-gray-900">Custom</span>
              <p className="text-sm text-gray-500 mt-1.5">Preço sob consulta</p>
            </div>

            <ul className="space-y-3 mb-7 flex-1">
              {[
                'Tudo do plano Pro',
                'Consultoria dedicada',
                'Integração com ERPs, CRMs, TMS e mais',
                'Múltiplos fluxos e processos',
                'Onboarding personalizado',
                'Suporte prioritário 24/7',
              ].map((item) => (
                <li key={item} className="flex items-start gap-2.5 text-sm text-gray-700">
                  <div className="w-5 h-5 rounded-full bg-indigo-50 flex items-center justify-center shrink-0 mt-0.5">
                    <Check className="w-3 h-3 text-indigo-600" />
                  </div>
                  <span>{item}</span>
                </li>
              ))}
            </ul>

            <button
              onClick={handleEnterprise}
              className="w-full py-3 px-4 rounded-xl text-sm font-bold text-white bg-indigo-600 hover:bg-indigo-700 transition-all shadow-sm hover:shadow-md flex items-center justify-center gap-2"
            >
              Falar com nosso time <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-gray-400 mt-8">
          Todos os planos incluem suporte por email e atualizações gratuitas.
        </p>

        {onVoltar && (
          <div className="text-center mt-5">
            <button
              onClick={onVoltar}
              className="text-sm text-gray-500 hover:text-gray-900 font-medium transition-colors flex items-center justify-center gap-1.5 mx-auto"
            >
              <ArrowLeft className="w-4 h-4" /> Voltar
            </button>
          </div>
        )}
      </div>
    </div>
  );

  if (modo === 'modal') {
    return (
      <div className="fixed inset-0 z-[9999] overflow-y-auto bg-black/40 backdrop-blur-sm">
        <div className="min-h-screen">{content}</div>
      </div>
    );
  }

  // Verificar se usuário é administrador (apenas quando renderizado como página/menu)
  const role = session?.usuario?.role;
  const isAdmin = role ? ['administrator', 'admin'].includes(role) : true;

  if (!isAdmin) {
    return (
      <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        <div className="text-center py-16">
          <ExclamationTriangleIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Acesso Restrito</h2>
          <p className="text-gray-500">
            Apenas administradores podem gerenciar planos.
          </p>
        </div>
      </div>
    );
  }

  return content;
};

export default Planos;
